import { styled } from "@mui/system";
import { Button, Icon as MuiIcon } from "@mui/material";
import { Formik } from "formik";
import React, { useCallback } from "react";
import PropTypes from "prop-types";

const CustomStyledButton = styled(Button)(
  ({ theme, size, variant, customvariant }) => {
    const isClearVariant = variant === "outlined" || variant === "text";
    const isDeleteVariant = customvariant === "delete";
    const sizeStyles = {
      small: {
        padding: theme.spacing(1, 2),
        fontSize: theme.typography.pxToRem(14),
        minWidth: 100,
        height: 32,
      },
      medium: {
        padding: theme.spacing(1.25, 2.5),
        fontSize: theme.typography.pxToRem(14),
        minWidth: 120,
        height: 48,
      },
      large: {
        padding: theme.spacing(1.5, 3),
        fontSize: theme.typography.pxToRem(16),
        minWidth: 160,
        height: 56,
      },
    };

    const defaultSizeStyles = sizeStyles[size] || sizeStyles.medium;

    return {
      ...defaultSizeStyles,
      borderRadius: size === "medium" ? theme.shape.sm : undefined,
      backgroundColor: isDeleteVariant
        ? "rgba(255, 0, 0, 0.05)"
        : isClearVariant
        ? "transparent"
        : theme.palette.primary.main,
      color: isDeleteVariant
        ? "red"
        : isClearVariant
        ? theme.palette.primary.main
        : theme.palette.primary.contrastText,
      border: isDeleteVariant
        ? `1px solid red`
        : isClearVariant
        ? `1px solid ${theme.palette.primary.main}`
        : `1px solid transparent`,
      // boxShadow: isClearVariant ? `0px 4px 12px ${theme.palette.grey[400]}` : "none",
      "&:hover": {
        backgroundColor: isDeleteVariant
          ? "rgba(255, 0, 0, 0.1)"
          : isClearVariant
          ? "transparent"
          : theme.palette.primary.dark,
        opacity: isClearVariant ? 1 : 0.9,
        border: isDeleteVariant
          ? `1px solid red`
          : isClearVariant
          ? `1px solid ${theme.palette.primary.main}`
          : `1px solid transparent`,
      },
      // icon color
      "& .MuiButton-startIcon": {
        marginRight: theme.spacing(0.1),
      },
      "& .MuiButton-endIcon": {
        marginLeft: theme.spacing(0.1),
      },
    };
  }
);

const FormikButton = ({
  type = "button",
  label = "Submit",
  variant = "contained",
  size = "medium",
  customvariant = "",
  onClick = () => {},
  sx = {},
  startIcon = null,
  endIcon = null,
  startIconSx = {},
  endIconSx = {},

  ...props
}) => {
  const { useContext, submitForm, resetForm } = Formik;

  const handleClick = useCallback(() => {
    if (type === "submit") {
      submitForm && submitForm();
    } else if (type === "reset") {
      resetForm && resetForm();
    } else if (type === "button") {
      onClick();
    }
  }, [type, submitForm, resetForm, onClick]);

  return (
    <CustomStyledButton
      type={type === "submit" || type === "reset" ? type : "submit"}
      variant={variant}
      size={size}
      customvariant={customvariant}
      onClick={handleClick}
      startIcon={<span style={startIconSx}>{startIcon}</span>}
      endIcon={<span style={endIconSx}>{endIcon}</span>}
      sx={{
        marginRight: (theme) => theme.spacing(1),
        ...sx,
      }}
      {...props}
    >
      {label}
    </CustomStyledButton>
  );
};

FormikButton.propTypes = {
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  label: PropTypes.string,
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  customvariant: PropTypes.oneOf(["delete"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  onClick: PropTypes.func,
  sx: PropTypes.object,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
};

export default FormikButton;
